import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import './components.css';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
}
export function Modal({ open, onClose, title, children, footer, size = 'md' }: ModalProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);
  if (!open) return null;
  return createPortal(
    <div className="k-modal__backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`k-modal k-modal--${size}`} role="dialog" aria-modal="true">
        {title && <header className="k-modal__header"><h3 className="k-panel-title">{title}</h3></header>}
        <div className="k-modal__body">{children}</div>
        {footer && <footer className="k-modal__footer">{footer}</footer>}
      </div>
    </div>,
    document.body,
  );
}

export interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: ReactNode;
  /** Texto explicando o efeito da ação. */
  message?: ReactNode;
  confirmLabel: ReactNode;
  cancelLabel: ReactNode;
  /** Ação destrutiva: botão vermelho. */
  danger?: boolean;
  loading?: boolean;
}
/** Confirmação padrão para ações destrutivas ou de efeito amplo (substitui window.confirm). */
export function ConfirmDialog({ open, onClose, onConfirm, title, message, confirmLabel, cancelLabel, danger = false, loading = false }: ConfirmDialogProps) {
  return (
    <Modal open={open} onClose={onClose} title={title} size="sm" footer={<>
      <button type="button" className="k-btn k-btn--secondary" onClick={onClose} disabled={loading}>{cancelLabel}</button>
      <button type="button" className={`k-btn ${danger ? 'k-btn--danger' : 'k-btn--primary'}`} onClick={onConfirm} disabled={loading} aria-busy={loading || undefined}>{loading ? <span className="k-spinner" aria-hidden /> : null}<span>{confirmLabel}</span></button>
    </>}>
      {message ? <p style={{ margin: 0 }}>{message}</p> : null}
    </Modal>
  );
}
